import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { StickyNote, Send, Trash2, CheckCircle2, Clock } from "lucide-react";

const ROLE = { admin: "Admin", user: "Equipo", driver: "Conductor" };

export default function NotesPage() {
  const [people, setPeople] = useState([]);
  const [sent, setSent] = useState([]);
  const [to, setTo] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const loadSent = () => api.get("/notes/sent").then((r) => setSent(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/team/people").then((r) => setPeople(r.data)).catch(() => {});
    loadSent();
  }, []);

  const send = async () => {
    if (!to || !text.trim()) return toast.error("Elige a quién y escribe la nota");
    setBusy(true);
    try {
      await api.post("/notes", { recipient_id: to, text });
      toast.success("Nota enviada. La verá al entrar en la web.");
      setText("");
      loadSent();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/notes/${id}`).catch(() => {});
    setSent((s) => s.filter((n) => n.id !== id));
  };

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-4xl" data-testid="notes-page">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Comunicación</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 mb-6 flex items-center gap-3">
        <StickyNote className="text-[#F5B301]" size={30} /> Notas
      </h1>

      <div className="border-2 border-black bg-white p-5 space-y-3 mb-8">
        <div className="text-[10px] uppercase tracking-widest">Para</div>
        <select value={to} onChange={(e) => setTo(e.target.value)} className="w-full h-11 border border-black px-3 bg-white text-sm" data-testid="note-recipient">
          <option value="">Elige un compañero o conductor…</option>
          {people.map((p) => <option key={p.id} value={p.id}>{p.name} · {ROLE[p.role] || p.role}</option>)}
        </select>
        <Textarea rows={3} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Ej.: Recuerda hacer la facturación y dar de alta a los 3 conductores"
          className="rounded-none border-black" data-testid="note-text" />
        <button onClick={send} disabled={busy} data-testid="note-send" className="inline-flex items-center gap-2 h-11 px-6 bg-black text-white text-xs font-semibold uppercase tracking-widest hover:bg-[#E63946] transition-colors disabled:opacity-50">
          <Send size={14} /> Dejar nota
        </button>
      </div>

      <h2 className="font-display font-bold text-lg mb-3">Notas enviadas</h2>
      <div className="space-y-2" data-testid="notes-sent-list">
        {sent.length === 0 && <p className="text-sm text-zinc-500">Aún no has dejado notas.</p>}
        {sent.map((n) => (
          <div key={n.id} className="border border-black bg-white p-4 flex items-start justify-between gap-3" data-testid={`note-sent-${n.id}`}>
            <div className="min-w-0">
              <div className="text-xs font-semibold">Para {n.recipient_name}</div>
              <p className="text-sm whitespace-pre-wrap mt-1">{n.text}</p>
              <div className={`text-[11px] mt-2 flex items-center gap-1 ${n.acknowledged ? "text-emerald-700" : "text-zinc-500"}`}>
                {n.acknowledged ? <CheckCircle2 size={12} /> : <Clock size={12} />}
                {n.acknowledged ? `Leída ${new Date(n.acknowledged_at).toLocaleString("es-ES")}` : "Pendiente de leer"}
              </div>
            </div>
            <button onClick={() => remove(n.id)} className="p-2 border border-black hover:bg-[#E63946] hover:text-white transition-colors" data-testid={`note-delete-${n.id}`} aria-label="Eliminar nota">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
